import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalData,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  getAdminCompanyRole,
  updateAdminCompanyRole,
} from "../../../../network/apis/adminCompanyRoles";
import {
  createEmptyPermissions,
  arePermissionsEqual,
  toPermissionsPayload,
} from "../../../Company-portal/TeamsPermissions/CreateRole/permissionModules";
import {
  normalizeCompanyRole,
  permissionsArrayToMap,
} from "../../../Company-portal/TeamsPermissions/roleMappers";
import {
  adminCompanyTeamRoutes,
  COMPANY_DETAILS_TEAM_TAB,
  TEAM_SUB_TABS,
} from "../adminCompanyTeamRoutes";

const useAdminViewRole = () => {
  const history = useHistory();
  const location = useLocation();
  const { companyId, id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.viewRole;
  const createCopy = teamCopy.createRole;
  const companiesList = messages[lang].companiesList;
  const { shared } = messages[lang];
  const { canEdit } = useAdminModulePermission(
    companyPermissionModules.rolesManagement,
  );

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [permissions, setPermissions] = useState(createEmptyPermissions);
  const editFromListRef = useRef(Boolean(location.state?.startInEdit));
  const autoEnteredEditRef = useRef(false);

  const goBackToList = useCallback(() => {
    if (!companyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }
    const returnSubtab =
      location.state?.returnSubtab ?? TEAM_SUB_TABS.employees;
    history.push(
      adminCompanyTeamRoutes.companyDetailsTabLocation(
        companyId,
        COMPANY_DETAILS_TEAM_TAB,
        returnSubtab,
      ),
    );
  }, [history, companyId, location.state?.returnSubtab]);

  useEffect(() => {
    if (!id) {
      goBackToList();
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getAdminCompanyRole(id)
      .then((response) => {
        if (cancelled) return;
        const normalized = normalizeCompanyRole(extractAdminPortalData(response));
        setRole(normalized);
        setPermissions(permissionsArrayToMap(normalized.permissions));
      })
      .catch((error) => {
        if (cancelled) return;
        setRole(null);
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
        goBackToList();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, goBackToList, shared.generalErrMsg, lang]);

  const roleName = role?.name || "";
  const isSystemRole = Boolean(role?.systemRole);
  const trimmedName = name.trim();
  const showNameError = editing && !isSystemRole && nameTouched && !trimmedName;

  const savedPermissions = useMemo(
    () =>
      role ? permissionsArrayToMap(role.permissions) : createEmptyPermissions(),
    [role],
  );

  const isEditDirty = useMemo(() => {
    if (!editing) return false;
    const nameChanged = !isSystemRole && trimmedName !== roleName;
    const permissionsChanged = !arePermissionsEqual(permissions, savedPermissions);
    return nameChanged || permissionsChanged;
  }, [editing, isSystemRole, trimmedName, roleName, permissions, savedPermissions]);

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
      {
        path: companyId
          ? adminCompanyTeamRoutes.companyDetailsTeamTab(
              companyId,
              location.state?.returnSubtab ?? TEAM_SUB_TABS.roles,
            )
          : "",
        label: companiesList.teamPermissionsTab,
      },
      {
        path: "",
        label: copy.titleTemplate.replace("{name}", roleName),
      },
    ],
    [
      companiesList.listBreadcrumb,
      companiesList.teamPermissionsTab,
      copy.titleTemplate,
      roleName,
      companyId,
      location.state?.returnSubtab,
    ],
  );

  const enterEdit = useCallback(() => {
    if (role) setName(role.name || "");
    setNameTouched(false);
    setEditing(true);
  }, [role]);

  useEffect(() => {
    if (
      role &&
      canEdit &&
      location.state?.startInEdit &&
      !autoEnteredEditRef.current
    ) {
      autoEnteredEditRef.current = true;
      enterEdit();
    }
  }, [role, location.state?.startInEdit, canEdit, enterEdit]);

  const exitEdit = useCallback(() => {
    if (editFromListRef.current) {
      goBackToList();
      return;
    }
    if (role) {
      setPermissions(permissionsArrayToMap(role.permissions));
      setName(role.name || "");
    }
    setNameTouched(false);
    setEditing(false);
  }, [goBackToList, role]);

  const cancelEdit = () => {
    if (updating) return;
    exitEdit();
  };

  const handleUpdate = async () => {
    if (!role || updating || !isEditDirty) {
      if (!isSystemRole) setNameTouched(true);
      return;
    }
    if (!isSystemRole && !trimmedName) {
      setNameTouched(true);
      return;
    }

    const data = {};
    if (!isSystemRole && trimmedName !== roleName) data.name = trimmedName;
    if (!arePermissionsEqual(permissions, savedPermissions)) {
      data.permissions = toPermissionsPayload(permissions);
    }
    if (Object.keys(data).length === 0) return;

    const displayName = isSystemRole ? roleName : trimmedName;

    setUpdating(true);
    try {
      const response = await updateAdminCompanyRole({
        id: role.id,
        data,
      });
      const updated = normalizeCompanyRole(extractAdminPortalData(response));
      setRole(updated);
      setPermissions(permissionsArrayToMap(updated.permissions));
      setName(updated.name || "");
      showSuccessMsg(copy.updateSuccess.replace("{name}", displayName), true);
      // Exit edit mode without calling exitEdit(): its `role` closure is still
      // the pre-save role and would revert the freshly-saved permissions/name
      // we just set above, leaving the view stale until a manual refresh.
      if (editFromListRef.current) {
        goBackToList();
      } else {
        setNameTouched(false);
        setEditing(false);
      }
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setUpdating(false);
    }
  };

  return {
    teamCopy,
    copy,
    createCopy,
    breadcrumbList,
    role,
    loading,
    editing,
    updating,
    permissions,
    setPermissions,
    canEdit,
    enterEdit,
    cancelEdit,
    handleUpdate,
    roleName,
    isSystemRole,
    name,
    setName,
    setNameTouched,
    showNameError,
    trimmedName,
    isEditDirty,
  };
};

export default useAdminViewRole;
